import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Button, CircularProgress, Typography, Alert, Tabs, Tab,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, InputAdornment, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, Divider, IconButton, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Straighten as MeasurementIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import axios from 'axios';

import { fetchBrand } from '../../api/brands';
import PatternBuilder from '../../components/SPIF/PatternBuilder';

// ── Measurement field labels (shared with the old standalone page) ────────────
const FIELD_LABELS = {
  chest: 'Chest', waist: 'Waist', hips: 'Hips', shoulder: 'Shoulder',
  inseam: 'Inseam', height: 'Height', sleeve_length: 'Sleeve Length',
  neck: 'Neck', thigh: 'Thigh', knee: 'Knee', bicep: 'Bicep',
};

// ── Client Measurements panel ─────────────────────────────────────────────────
const ClientMeasurementsPanel = ({ brandId }) => {
  const [profiles, setProfiles]   = useState([]);
  const [filtered, setFiltered]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [search, setSearch]       = useState('');
  const [selected, setSelected]   = useState(null);
  const [editEmail, setEditEmail] = useState('');
  const [editName, setEditName]   = useState('');
  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [copied, setCopied]       = useState(false);

  const load = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/api/measurement-requests/profiles?brandId=${brandId}`);
      const data = res.data?.profiles || [];
      setProfiles(data);
      setFiltered(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(q
      ? profiles.filter(p =>
          p.email?.toLowerCase().includes(q) || p.name?.toLowerCase().includes(q))
      : profiles
    );
  }, [search, profiles]);

  const handleOpen = (profile) => {
    setSelected(profile);
    setEditEmail(profile.email || '');
    setEditName(profile.name || '');
    setSaveError(null);
  };

  const handleClose = () => { setSelected(null); setSaveError(null); };

  const handleSave = async () => {
    if (!editEmail.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      await axios.patch(`/api/measurement-requests/profiles/${selected._id}`, {
        email: editEmail.trim().toLowerCase(),
        name: editName.trim(),
      });
      await load();
      handleClose();
    } catch (err) {
      setSaveError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const copyEmail = (email) => {
    navigator.clipboard.writeText(email);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const measurementEntries = (m = {}) =>
    Object.entries(m).filter(([, v]) => v != null && v !== '');

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>Client Measurements</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Submitted via measurement request links
          </Typography>
        </Box>
        <Chip label={`${filtered.length} profiles`} variant="outlined" />
      </Box>

      <TextField
        fullWidth
        placeholder="Search by name or email…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
          ),
        }}
      />

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && !error && (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Measurements</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No profiles found
                  </TableCell>
                </TableRow>
              )}
              {filtered.map(p => (
                <TableRow key={p._id} hover sx={{ cursor: 'pointer' }} onClick={() => handleOpen(p)}>
                  <TableCell>{p.name || <span style={{ color: '#999' }}>—</span>}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {p.email}
                      <Tooltip title={copied ? 'Copied!' : 'Copy email'}>
                        <IconButton size="small" onClick={e => { e.stopPropagation(); copyEmail(p.email); }}>
                          <CopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {measurementEntries(p.measurements).slice(0, 4).map(([k, v]) => (
                        <Chip key={k} label={`${FIELD_LABELS[k] || k}: ${v}`} size="small" variant="outlined" />
                      ))}
                      {measurementEntries(p.measurements).length > 4 && (
                        <Chip label={`+${measurementEntries(p.measurements).length - 4} more`} size="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{p.measurementUnit || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={p.accountClaimStatus || 'unclaimed'}
                      size="small"
                      color={p.accountClaimStatus === 'claimed' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {p.lastSubmittedAt ? new Date(p.lastSubmittedAt).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={e => { e.stopPropagation(); handleOpen(p); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Detail / Edit dialog */}
      <Dialog open={!!selected} onClose={handleClose} maxWidth="sm" fullWidth>
        {selected && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              Measurement Profile
              <IconButton onClick={handleClose}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                  <TextField
                    label="Email"
                    fullWidth
                    value={editEmail}
                    onChange={e => setEditEmail(e.target.value)}
                    helperText="Update to match the customer's portal sign-up email"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Name"
                    fullWidth
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    size="small"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>
                Measurements ({selected.measurementUnit || 'in'})
              </Typography>
              <Grid container spacing={1}>
                {measurementEntries(selected.measurements).map(([k, v]) => (
                  <Grid item xs={6} sm={4} key={k}>
                    <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {FIELD_LABELS[k] || k}
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>{v}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {saveError && <Alert severity="error" sx={{ mt: 2 }}>{saveError}</Alert>}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>Cancel</Button>
              <Button onClick={handleSave} variant="contained" disabled={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────
const PatternLibraryPage = () => {
  const { brandId } = useParams();
  const navigate = useNavigate();

  // Mock user - in production, get from auth context
  const userId = '507f1f77bcf86cd799439011';

  const [brand, setBrand]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [tab, setTab]       = useState(0);

  useEffect(() => {
    let mounted = true;

    const loadBrand = async () => {
      try {
        setLoading(true);
        setError(null);
        const brandData = await fetchBrand(brandId);
        if (!mounted) return;
        setBrand(brandData);
      } catch (err) {
        console.error('Failed to load brand:', err);
        if (mounted) setError('Unable to load brand details.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (brandId) {
      loadBrand();
    } else {
      setLoading(false);
    }

    return () => { mounted = false; };
  }, [brandId]);

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Pattern Builder
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create patterns and view submitted client measurements for {brand?.name || 'this brand'}
          </Typography>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab icon={<BuildIcon />} iconPosition="start" label="Pattern Builder" />
          <Tab icon={<MeasurementIcon />} iconPosition="start" label="Client Measurements" />
        </Tabs>
      </Box>

      {/* Tab panels */}
      {tab === 0 && (
        loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <PatternBuilder brandId={brandId} brand={brand} userId={userId} />
        )
      )}

      {tab === 1 && (
        <ClientMeasurementsPanel brandId={brandId} />
      )}
    </Box>
  );
};

export default PatternLibraryPage;
